import type { HierarcicalObjectReference, ObjectData, ObjectId } from "@novorender/data-js-api";
import type { Predicate } from "@procore/bim-index";
import type { ReadonlyVec4 } from "gl-matrix";
import pMap from "p-map";

import type { ObjectVisibilityStats, ObjectVisibilityStatsRequest } from "dataAccess";
import { NodeType } from "types/misc";
import type { SelectionSet } from "utils/selectionSet";

/**
 * Scene visibility stats out of a BIM index, shared by both index backends through
 * {@link VisibilityStatsIndex}.
 *
 * Counted off id sets, which name objects and not containers: a container whose whole
 * subtree is gone still counts as showing. Rolling the counts up a filtered tree would
 * settle those and no more — the index holds bounds for only part of the tree, so the
 * figures are estimates either way.
 */

/** A top-level node. Its path is its name. */
export interface VisibilityStatsFile {
    id: ObjectId;
    path: string;
    /** Objects in its subtree, itself included. */
    descendantCount: number;
    hasChildren: boolean;
}

/**
 * A predicate's matches and their descendants, kept apart so
 * {@link filesShowingSomething} can ask about the matches alone. A backend that gains
 * nothing from the split answers everything as {@link matches}.
 */
export interface SectionedOutIds {
    matches: Uint32Array;
    /** Everything under the matches, nested matches included. */
    descendants: Uint32Array;
}

/**
 * The reads the stats need. Every method answers `undefined` for a read that did not
 * deliver — an abort, or a scan cut short that resuming could not finish — except
 * {@link getDescendantIds}, which answers the empty set.
 */
export interface VisibilityStatsIndex {
    topLevelFiles(signal?: AbortSignal): Promise<VisibilityStatsFile[] | undefined>;
    matchIdsWithDescendants(predicate: Predicate, signal?: AbortSignal): Promise<SectionedOutIds | undefined>;
    /** Which of `files` hold at least one of `ids`. A file's own id counts as held by it. */
    filesHolding(
        files: readonly VisibilityStatsFile[],
        ids: SelectionSet,
        signal?: AbortSignal,
    ): Promise<Set<ObjectId> | undefined>;
    /** A node's subtree, the node itself excluded. */
    getDescendantIds(object: HierarcicalObjectReference, signal?: AbortSignal): Promise<Uint32Array>;
}

const SUBTREE_READ_CONCURRENCY = 6;

/**
 * The answer for an abandoned read. Figures absent rather than `0`, which would read as
 * "nothing excluded", and the section-plane flag with them: it describes a count that is
 * not here.
 */
const EMPTY_STATS: ObjectVisibilityStats = {};

const EMPTY_ID_ARRAY = new Uint32Array();

/**
 * `hidden` adds what the planes cut to the hidden set; `isolated` removes it from the
 * visible set. The planes' matches are taken with their subtrees, since boundless children
 * of a cut node match no half-space themselves.
 *
 * An abort answers {@link EMPTY_STATS} rather than rejecting, per the interface.
 */
export async function readObjectVisibilityStats(
    index: VisibilityStatsIndex,
    request: ObjectVisibilityStatsRequest,
): Promise<ObjectVisibilityStats> {
    const { sectionPlanes, sectionPlaneMode, signal } = request;
    const sectionedOut = sectionPlanes.length
        ? await index.matchIdsWithDescendants(sectionedOutPredicate(sectionPlanes, sectionPlaneMode ?? "union"), signal)
        : NOTHING_SECTIONED_OUT;
    if (!sectionedOut || signal?.aborted) {
        return EMPTY_STATS;
    }
    // Only the ignored ids the app named; their undiscovered descendants stay in.
    const ignored = request.ignoredObjectIds?.sortedIds ?? EMPTY_ID_ARRAY;
    if (request.mode === "isolated") {
        const visibleIds = request.visibleIds
            .withRemoved(sectionedOut.matches)
            .withRemoved(sectionedOut.descendants)
            .withRemoved(ignored);
        return isolatedModeStats(index, request, visibleIds);
    }
    const excludedIds = request.hiddenIds
        .withAdded(sectionedOut.matches)
        .withAdded(sectionedOut.descendants)
        .withRemoved(ignored);
    return hiddenModeStats(index, request, excludedIds, excludedIds.withRemoved(sectionedOut.descendants));
}

const NOTHING_SECTIONED_OUT: SectionedOutIds = { matches: EMPTY_ID_ARRAY, descendants: EMPTY_ID_ARRAY };

/** Every node in the tree, grouping rows included. */
function sceneObjectCount(files: readonly VisibilityStatsFile[]): number {
    return files.reduce((count, file) => count + file.descendantCount, 0);
}

/** The scene's files less the ignored ones, which takes their subtrees out of {@link sceneObjectCount}. */
async function sceneFiles(
    index: VisibilityStatsIndex,
    request: ObjectVisibilityStatsRequest,
    signal?: AbortSignal,
): Promise<VisibilityStatsFile[] | undefined> {
    const files = await index.topLevelFiles(signal);
    return files?.filter((file) => !(request.ignoredObjectIds?.has(file.id) ?? false));
}

/** @param holdingIds The part of `excludedIds` {@link filesShowingSomething} asks `filesHolding` about. */
async function hiddenModeStats(
    index: VisibilityStatsIndex,
    request: ObjectVisibilityStatsRequest,
    excludedIds: SelectionSet,
    holdingIds: SelectionSet,
): Promise<ObjectVisibilityStats> {
    const { includeVisibleFiles, signal } = request;
    let visibleFileCount: number | undefined;
    if (includeVisibleFiles) {
        // A file listing that did not arrive costs only its own figure — the excluded
        // count is the named set, already in hand.
        const files = await sceneFiles(index, request, signal);
        visibleFileCount = files && (await filesShowingSomething(index, files, excludedIds, holdingIds, signal));
    }
    return {
        visibleFileCount,
        excludedObjectCount: excludedIds.size,
        excludedObjectsRespectSectionPlanes: true,
    };
}

/**
 * Both figures ride on the file listing here — the scene total the excluded count is
 * subtracted from is the files' `descendantCount`s — so a listing that did not arrive
 * takes both, where {@link hiddenModeStats} keeps its count.
 */
async function isolatedModeStats(
    index: VisibilityStatsIndex,
    request: ObjectVisibilityStatsRequest,
    visibleIds: SelectionSet,
): Promise<ObjectVisibilityStats> {
    const { includeVisibleFiles, signal } = request;
    // Read even when files were not asked for: their `descendantCount`s are the scene total.
    const files = await sceneFiles(index, request, signal);
    if (!files) {
        return EMPTY_STATS;
    }
    let visibleFileCount: number | undefined;
    if (includeVisibleFiles) {
        const holding = await index.filesHolding(files, visibleIds, signal);
        visibleFileCount = holding?.size;
    }
    return {
        visibleFileCount,
        // An id the tree does not account for would otherwise take the count below zero.
        excludedObjectCount: Math.max(sceneObjectCount(files) - visibleIds.size, 0),
        excludedObjectsRespectSectionPlanes: true,
    };
}

/**
 * Files still showing something, `undefined` for an abandoned read. Each step reads only
 * what the cheaper one before it could not settle:
 *
 * 1. A file with more objects than the excluded set cannot be fully excluded.
 * 2. A file holding none of the excluded ids is fully visible.
 * 3. The rest have their subtrees read.
 *
 * @param holdingIds What step 2 asks about: `excludedIds` less the section-plane
 *   descendants. A file holding a cut descendant also holds the match above it.
 */
async function filesShowingSomething(
    index: VisibilityStatsIndex,
    files: VisibilityStatsFile[],
    excludedIds: SelectionSet,
    holdingIds: SelectionSet,
    signal?: AbortSignal,
): Promise<number | undefined> {
    const candidates = files.filter((file) => file.descendantCount <= excludedIds.size);
    if (!candidates.length) {
        return files.length;
    }

    const holding = await index.filesHolding(candidates, holdingIds, signal);
    if (!holding) {
        return undefined;
    }
    const uncertain = candidates.filter((file) => holding.has(file.id));

    const stillVisible = await pMap(uncertain, (file) => showsSomething(index, file, excludedIds, signal), {
        concurrency: SUBTREE_READ_CONCURRENCY,
        signal,
    }).catch((e) => {
        // `pMap` rejects when its signal fires, where the interface answers an abort with
        // an empty value. Anything else is a real failure and still throws.
        if (signal?.aborted) {
            return undefined;
        }
        throw e;
    });
    if (!stillVisible) {
        return undefined;
    }
    return files.length - uncertain.length + stillVisible.filter(Boolean).length;
}

async function showsSomething(
    index: VisibilityStatsIndex,
    file: VisibilityStatsFile,
    excludedIds: SelectionSet,
    signal?: AbortSignal,
): Promise<boolean> {
    if (!file.hasChildren) {
        // Only the file's own id can have put it here.
        return false;
    }
    const descendants = await index.getDescendantIds(fileReference(file), signal);
    if (!descendants.length) {
        // A read that did not arrive. Left visible: one file too many reads as an estimate,
        // one too few as a real change.
        return true;
    }
    return !descendants.every((descendant) => excludedIds.has(descendant));
}

/** A file as a subtree read's reference. `loadMetaData` throws; nothing here opens the object. */
export function fileReference(file: VisibilityStatsFile): HierarcicalObjectReference {
    return {
        id: file.id,
        path: file.path,
        type: file.hasChildren ? NodeType.Internal : NodeType.Leaf,
        async loadMetaData(): Promise<ObjectData> {
            throw new Error(`Object ${file.id} has no metadata available`);
        },
    };
}

/** The engine's vector: a tuple, where the API's DTO takes `{x, y, z}`. */
type Vector3 = [number, number, number];

/**
 * What the section planes cut away.
 *
 * The engine's half-space matches what a plane *leaves*, so the cut is the negation of the
 * conjunction — inverting a normal instead would match everything straddling the plane.
 * `union` mode survives by surviving every plane (`AND`), `intersection` by surviving one
 * (`OR`).
 *
 * Fenced by {@link hasBoundsPredicate}: an object with no bounds satisfies no half-space
 * and would otherwise land in the complement.
 *
 * @param sectionPlanes At least one.
 */
function sectionedOutPredicate(sectionPlanes: readonly ReadonlyVec4[], mode: "union" | "intersection"): Predicate {
    return {
        operator: "AND",
        predicates: [
            {
                operator: mode === "intersection" ? "OR" : "AND",
                negated: true,
                predicates: sectionPlanes.map(clippingHalfSpace),
            },
            hasBoundsPredicate(),
        ],
    };
}

/** Objects the index holds bounds for. `$bounds` needs `@procore/bim-index` 8.3.0+. */
function hasBoundsPredicate(): Predicate {
    return { operator: "DEFINED", property: "$bounds" };
}

function clippingHalfSpace(plane: ReadonlyVec4): Predicate {
    return halfSpace(planePoint(plane), [plane[0], plane[1], plane[2]]);
}

/** The point on a `normalOffset` plane: the unit normal scaled by the offset. */
function planePoint([x, y, z, offset]: ReadonlyVec4): Vector3 {
    return [x * offset, y * offset, z * offset];
}

/** Plain tuples, not a `gl-matrix` typed array, which would serialize as `{"0": …}`. */
function halfSpace(point: Vector3, normal: Vector3): Predicate {
    return { operator: "BOUNDS_HALF_SPACE", point, normal };
}
